/* zerbay-pdp.js (v2, hz/42): ES module printed by sections/zerbay-product.liquid (module scripts run after parsing).
   Later topics add functions to this file (size guide in hz/47); they do not rewrite the
   structure: one init(root) per .zb-pdp[data-zb-pdp] root.
   v0 (hz/40): the address marker of a gated page (PDPC-17, today ATL snippets/product.liquid:881-960).
   v1 (hz/41): unit tabs (PDPC-21), the ProductSelectEvent hook with after-morph callbacks, the gated price (PDPC-10) and the
   zb:pdp-state event (PLAN section 6). The gate itself is closed by the server (inline style + backstop in the section).
   v2 (hz/42): the buy state. The gate opener (size picks, colour picks, ProductSelectEvent check, close), the announcement,
   "Select your size", and the add path (the form's submit goes through window.Zerbay.cartLock, contract B in
   audit/.../pdp-buy-box/contracts.md).
   v3 (hz/47): the size guide dialog (PDPC-24): stock cells, chosen row, scroll lock, focus. No console logging. */

import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig, unlockScroll } from '@theme/utilities';

const MARK = 'zb-color';
const STATE_SELECTOR = 'script[data-zb-pdp-state]';
const started = new WeakSet();
const states = new WeakMap(); // root -> parsed state island (or null)
const ctxs = new WeakMap(); // root -> buy state (see makeCtx)
const FORM_SELECTOR = 'form[id^="BuyButtons-ProductForm-"]';
const ROOT_SELECTOR = '.zb-pdp[data-zb-pdp]';
// captured once, at module start: did the page load with the #zb-color marker? (a ?variant= link without it is a keep_size arrival)
const STARTED_MARKED = hasMarker(window.location.hash);
const suspended = new WeakSet();

/** A Horizon route (Theme.routes). `const Theme` in snippets/scripts.liquid is a global lexical binding, not a window property. */
function route(name, fallback) {
  return typeof Theme !== 'undefined' ? (Theme.routes?.[name] ?? fallback) : fallback;
}

/** The English value of the checked colour tile, or null. */
function checkedColour(root) {
  const input = root.querySelector('input[data-zb-colour-value]:checked');
  return input ? input.getAttribute('data-zb-colour-value') : null;
}

/** Parses the state island of a root. Returns null when it is missing or not valid JSON. */
function readState(root) {
  try {
    const script = root.querySelector(STATE_SELECTOR);
    if (!script) return null;
    const state = JSON.parse(script.textContent);
    return state && typeof state === 'object' ? state : null;
  } catch (error) {
    return null;
  }
}

/** The gate is closed while the root says so. The attribute is the single source of truth. */
function isGated(root) {
  return root.getAttribute('data-zb-size-chosen') === 'false';
}

/** A hash counts as marked when it starts with #zb-color (the rest after & is another fragment and is kept). */
function hasMarker(hash) {
  return hash.indexOf(`#${MARK}`) === 0;
}

/**
 * While the root is gated, makes the address hash start with #zb-color and keeps any other fragment after "&".
 * Called explicitly (hz/42's close path, a failed keep_size open) it also ends a suspension; the event hooks check
 * `suspended` first and never reach this while suspended.
 */
function ensureMarker(root) {
  if (!isGated(root)) return;
  suspended.delete(root); // an explicit call ends a suspension (only the hooks in initMarker stay inert while suspended)
  const hash = window.location.hash;
  if (hasMarker(hash)) return;
  const next = `#${MARK}${hash.length > 1 ? `&${hash.slice(1)}` : ''}`;
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + next);
}

/** Runs ensureMarker where a failing history write must never break the page. */
function safeEnsureMarker(root) {
  try {
    ensureMarker(root);
  } catch (error) {
    // the marker is a convenience for the address bar only
  }
}

/**
 * Stops the marker for a root: no write now, and the re-ensure hooks do nothing until the suspension ends. Used by
 * initMarker only (keep_size arrivals). Every gate-close path (hz/42) calls ensureMarker / resumeMarker, which ends it.
 */
function suspendMarker(root) {
  suspended.add(root);
}

/**
 * Lets the marker work again for a root. If the root is still gated, the marker is restored at once (a plain page address
 * then carries #zb-color again); a root that is no longer gated keeps no marker. Unlike ensureMarker, it also ends the suspension when
 * the root is open.
 */
function resumeMarker(root) {
  suspended.delete(root);
  safeEnsureMarker(root);
}

/** The parsed state island of a root, as stored by init (null when missing or invalid, undefined before init). For later topics. */
function stateOf(root) {
  return states.get(root);
}

/** The address marker: kept in the address while the page is gated (arrival, reload, back / forward, hash edits). */
function initMarker(root) {
  if (!isGated(root)) return;

  // A ?variant= arrival without the marker is a keep_size link: hz/42 opens the gate for it, so the marker starts suspended
  // (no initial write; the hooks below are registered but inert until ensureMarker / resumeMarker ends the suspension, so a later `load` cannot add it early).
  if (root.getAttribute('data-zb-arrival') === 'variant' && !hasMarker(window.location.hash)) suspendMarker(root);

  const again = () => {
    if (suspended.has(root)) return;
    safeEnsureMarker(root);
  };
  again();
  window.addEventListener('hashchange', again);
  window.addEventListener('popstate', again);
  window.addEventListener('load', again);
}

/** zb:pdp-state (PLAN section 6): other blocks listen to it on the root. variantId / size are filled while a size is chosen. */
function emitState(root) {
  const ctx = ctxs.get(root);
  const chosen = !!ctx && ctx.chosenSize !== null;
  const detail = {
    sizeChosen: root.getAttribute('data-zb-size-chosen') !== 'false',
    stock: root.dataset.zbStock,
    variantId: chosen ? ctx.expectedId : null,
    colour: checkedColour(root),
    size: chosen ? ctx.chosenSize : null,
    unit: root.dataset.zbUnit,
    pending: root.hasAttribute('data-zb-pending'),
  };
  // late listeners (blocks that start after this module) read the latest detail from the root instead of waiting for an event
  root.zbPdpState = detail;
  root.dispatchEvent(new CustomEvent('zb:pdp-state', { bubbles: true, detail }));
}

/** Unit tabs (PDPC-21). The root attribute drives the numbers and the pressed look in CSS; aria-pressed follows it. */
function applyPressed(root) {
  const unit = root.dataset.zbUnit;
  root.querySelectorAll('[data-zb-unit-tab]').forEach((tab) => {
    tab.setAttribute('aria-pressed', String(tab.getAttribute('data-zb-unit-tab') === unit));
  });
}

function initUnits(root) {
  root.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-zb-unit-tab]') : null;
    if (!tab || !root.contains(tab)) return;
    root.dataset.zbUnit = tab.getAttribute('data-zb-unit-tab');
    applyPressed(root);
    emitState(root);
  });
}

/** Gated price (PDPC-10): one sibling before Horizon's price container shows the lowest price of the checked colour. */
function syncGatedPrice(root) {
  const state = states.get(root);
  const colour = checkedColour(root);
  const entry = state && state.gated && state.colourMin && colour !== null ? state.colourMin[colour] : undefined;
  const container = root.querySelector('product-price [ref="priceContainer"]');
  let span = root.querySelector('.zb-gated-price');
  if (!entry || !container) {
    if (span) span.remove();
    return;
  }
  if (!span || span.nextElementSibling !== container) {
    if (span) span.remove();
    span = document.createElement('span');
    span.className = 'zb-gated-price';
    container.before(span);
  }
  if (span.textContent !== entry) span.textContent = entry;
}

function initGatedPrice(root) {
  syncGatedPrice(root);
  // the price follows the colour at once; the picker fetch (and Horizon's price swap) comes later
  root.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.matches('input[data-zb-colour-value]')) syncGatedPrice(root);
  });
}

/** Instant colour feedback (today ATL product-options.liquid:2312-2320): the label and the sold-out marks follow the picked
    colour at once, from the state island; the picker morph that follows confirms the same values. */
function syncColourFeedback(root, input) {
  const label = root.querySelector('[data-zb-colour-label]');
  const display = input.getAttribute('data-zb-colour-display');
  if (label && display !== null && label.textContent !== display) label.textContent = display;

  // no-photos note (PDPC-20): shown when the chosen colour has no photos and another colour has; the server render confirms
  const noPhotosNote = root.querySelector('[data-zb-no-photos-note]');
  if (noPhotosNote) {
    const others = Array.from(root.querySelectorAll('input[data-zb-colour-value]')).filter((other) => other !== input);
    noPhotosNote.hidden = !(input.getAttribute('data-zb-has-photos') === 'false' && others.some((other) => other.getAttribute('data-zb-has-photos') === 'true'));
  }

  const state = states.get(root);
  const variants = state && state.variants && typeof state.variants === 'object' ? state.variants : null;
  const colour = input.getAttribute('data-zb-colour-value');
  if (!variants || colour === null) return;
  if (!state.colourIndex || !state.sizeIndex) return; // fail-closed island (option positions unknown): leave the server's marks alone
  const note = root.querySelector('[id^="ZbSoldOut-"]');
  const list = Object.keys(variants).map((id) => variants[id]);
  root.querySelectorAll('.zb-size__item input').forEach((radio) => {
    const chip = radio.nextElementSibling;
    const size = radio.value;
    const match = list.find((entry) => entry && entry.c === colour && entry.s === size);
    const soldOut = !match || match.st !== 'in_stock';
    if (chip) {
      if (soldOut) chip.setAttribute('data-zb-sold-out', 'true');
      else chip.removeAttribute('data-zb-sold-out');
    }
    if (soldOut && note) radio.setAttribute('aria-describedby', note.id);
    else radio.removeAttribute('aria-describedby');
  });
}

function initColourFeedback(root) {
  root.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof Element && input.matches('input[data-zb-colour-value]')) syncColourFeedback(root, input);
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   hz/42: buy state. Per root: ctx = { state, ready, gatedProduct, chosenSize, expectedId, pendingPromise, adding, ... }.
   The root attributes stay the single source of truth for CSS; ctx holds what only the script needs.
   One colour tap runs in a fixed order: the window-capture click (the colour branch below: keep the chosen size or close),
   then the change event (hz/41's instant label / sold-out marks and the gated price), then Horizon's fetch and the morph.
   --------------------------------------------------------------------------------------------------------------------- */

function makeCtx(root) {
  return {
    state: stateOf(root), // the island parsed once by init (no second parse)
    gatedProduct: root.hasAttribute('data-zb-size-chosen'),
    ready: false, // false until the add handler is installed AND product-form-component and variant-picker are defined (3b)
    chosenSize: null, // raw size value the shopper picked, or null while the gate is closed
    expectedId: null, // island variant id (string) of the checked colour x chosenSize
    pendingPromise: null, // the latest ProductSelectEvent promise of this root's picker
    adding: false,
    busy: false, // the add button shows its busy state (re-applied after a button morph)
    rememberedRadio: null, // the size radio of a trusted pick that arrived before ready
    lastSize: null, // the size value that is checked (click / change / after every picker morph): a re-tap has no change event
    keepSizeArrival: root.getAttribute('data-zb-arrival') === 'variant' && !STARTED_MARKED,
    arrivalId: readArrivalId(root), // the variant id the SERVER selected for this URL (string of digits), or null: keep_size opens only for it
    focusPick: null, // { colour, value } of a keyboard pick, for restoring focus after the morph
    liveTimer: 0,
    changeGen: 0, // counts the radio change events of this root (the stamp of a scheduled URL reconcile)
    buyData: readBuyData(root),
  };
}

function readBuyData(root) {
  try {
    const script = root.querySelector('script[data-zb-buy-data]');
    const data = script ? JSON.parse(script.textContent) : null;
    return data && typeof data === 'object' ? data : {};
  } catch (error) {
    return {};
  }
}

/** The id the server selected (data-zb-arrival-variant of the buy-state block), or null: no block, no attribute or no number = fail closed. */
function readArrivalId(root) {
  const holder = root.querySelector('[data-zb-buy-state]');
  const raw = holder ? holder.getAttribute('data-zb-arrival-variant') : null;
  return raw !== null && /^\d+$/.test(raw.trim()) ? raw.trim() : null;
}

function ctxOf(node) {
  const root = node instanceof Element ? node.closest(ROOT_SELECTOR) : null;
  return root && started.has(root) ? { root, ctx: ctxs.get(root) } : null;
}

function sizeRadios(root) {
  return Array.from(root.querySelectorAll('.zb-picker fieldset.zb-size input[type="radio"]'));
}

/** The value of the product form's hidden variant id (string), or null when there is no form or no field. */
function formVariantId(root) {
  const form = root.querySelector(FORM_SELECTOR);
  const hidden = form && form.querySelector('input[name="id"]');
  return hidden ? String(hidden.value) : null;
}

/** Island lookup: the id (string) of the variant with exactly this colour and size value, or null. */
function variantFor(ctx, colour, size) {
  const variants = ctx.state && ctx.state.variants;
  if (!variants || colour === null || size === null) return null;
  const ids = Object.keys(variants);
  for (let i = 0; i < ids.length; i += 1) {
    const entry = variants[ids[i]];
    if (entry && entry.c === colour && entry.s === size) return ids[i];
  }
  return null;
}

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

/** Removes the #zb-color token from the address (the rest of the fragment is kept). */
function removeMarker() {
  try {
    const hash = window.location.hash;
    if (!hasMarker(hash)) return;
    const rest = hash.slice(1 + MARK.length);
    if (rest !== '' && rest.charAt(0) !== '&') return; // not our token (#zb-colorful): leave it
    const kept = rest.replace(/^&/, '');
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + (kept ? `#${kept}` : ''));
  } catch (error) {
    // the address is a convenience only
  }
}

/** Writes ?variant=<id> (path, other params and the fragment kept). Horizon does it on a change; a re-tap has none. */
function writeVariantUrl(id) {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === id) return;
    url.searchParams.set('variant', id);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch (error) {
    // the address is a convenience only
  }
}

function addErrorLine(root) {
  return root.querySelector('[data-zb-add-error]');
}

function clearAddError(root) {
  const line = addErrorLine(root);
  if (!line) return;
  line.textContent = ''; // the role=alert node stays rendered; the CSS collapses it while it is empty
}

function showAddError(root, ctx, message) {
  const line = addErrorLine(root);
  if (!line) return;
  line.textContent = message || ctx.buyData.error || '';
}

/** PDPC-22: the price (and the sold-out word) of the picked variant, into the polite region. Today ATL product.liquid:1137-1162. */
function announce(root, ctx) {
  const live = root.querySelector('[data-zb-live]');
  const entry = ctx.state && ctx.state.variants && ctx.state.variants[ctx.expectedId];
  if (!live || !entry) return;
  const parts = [];
  if (entry.p) parts.push(entry.p);
  if (entry.st !== 'in_stock' && ctx.buyData.soldOut) parts.push(ctx.buyData.soldOut);
  if (!parts.length) return;
  live.textContent = '';
  clearTimeout(ctx.liveTimer);
  ctx.liveTimer = setTimeout(() => {
    parts.forEach((text) => {
      const span = document.createElement('span');
      span.textContent = text;
      live.appendChild(span);
      live.appendChild(document.createTextNode(' '));
    });
  }, 50);
}

/** Opens the gate for a picked size (3c). The click precedes the change on which Horizon builds its URL from location.href,
    so removing the marker here makes Horizon write ?variant= without it. */
function open(root, ctx, size, id, writeUrl, announcePrice = true) {
  ctx.chosenSize = size;
  ctx.expectedId = id;
  root.setAttribute('data-zb-size-chosen', 'true');
  root.setAttribute('data-zb-stock', ctx.state.variants[id].st);
  removeMarker();
  if (writeUrl) writeVariantUrl(id);
  clearAddError(root);
  if (announcePrice) announce(root, ctx); // a keep_size arrival is no pick: nothing to announce (spec 3g, PDPC-22)
  emitState(root);
}

/** Closes the gate (3f): no size, a size that does not exist in this colour, a wrong answer, an error. The radio stays checked
    (Horizon needs one); the CSS shows no chosen chip while the root says "false". */
function close(root, ctx) {
  ctx.chosenSize = null;
  ctx.expectedId = null;
  ctx.focusPick = null;
  // an announcement that is still waiting for its 50 ms timer must not name a price after the gate closed
  clearTimeout(ctx.liveTimer);
  const live = root.querySelector('[data-zb-live]');
  if (live) live.textContent = '';
  root.setAttribute('data-zb-size-chosen', 'false');
  safeEnsureMarker(root); // ends a keep_size suspension too
  syncGatedPrice(root);
  clearAddError(root);
  emitState(root);
}

/**
 * A trusted pick of a size radio while the buy state is ready. A radio that was ALREADY checked gets no change event, so
 * Horizon never hears of the tap: when the form already holds that variant (or a variant change is in flight; checkAnswer
 * verifies its answer, but an in-flight change is not guaranteed to bring this variant) the gate opens and ?variant= is written here; otherwise the form holds another variant (a size checked before
 * any script ran, a restored form state), so the gate opens and a bubbling change is dispatched, which makes Horizon fetch
 * and write the URL (checkAnswer then verifies the answer).
 */
function pickSize(root, ctx, radio, wasChecked) {
  const size = radio.value;
  const id = variantFor(ctx, checkedColour(root), size);
  if (id === null) {
    close(root, ctx);
    return;
  }
  let writeUrl = false;
  let replay = false;
  if (wasChecked) {
    if (ctx.pendingPromise || formVariantId(root) === id) writeUrl = true;
    else replay = true;
  }
  open(root, ctx, size, id, writeUrl);
  if (replay) radio.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Brings the address back to the pick of the shopper. Horizon's variantChanged writes the URL from the clicked radio's
 * data-variant-id after `await yieldToMainThread()`, which can run before OR after the fetch resolves, so the repair made at
 * the resolve (checkAnswer) can be overwritten. Called at the resolve and again after Horizon's deferred write. Only the latest
 * change of the root acts. Open: ?variant=<expectedId>, marker removed. Gated: the marker is ensured.
 */
function reconcileUrl(root, ctx, generation) {
  if (generation !== ctx.changeGen) return;
  if (isGated(root)) {
    safeEnsureMarker(root);
    return;
  }
  if (ctx.expectedId === null) return;
  removeMarker();
  writeVariantUrl(ctx.expectedId);
}

/** Queues reconcileUrl behind Horizon's deferred write. The window-capture change listener runs before the picker's own, so
    these hops are queued first: a second timer hop is queued from inside the first (after Horizon's timers), and the animation
    frame chain is the production order: Horizon 4.3.0 always writes the URL through requestAnimationFrame + setTimeout
    (utilities.js:15, :20-24, :755; its own Scheduler has no `yield`), after the timer hops above. */
function scheduleReconcile(root, ctx) {
  ctx.changeGen += 1;
  const generation = ctx.changeGen;
  const run = () => reconcileUrl(root, ctx, generation);
  setTimeout(() => {
    run();
    setTimeout(run, 0);
  }, 0);
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      setTimeout(() => setTimeout(run, 0), 0);
    });
  }
}

/** 3c / 3d: one window-capture click listener for every size and colour radio of every root. */
function onPickClick(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
  const found = ctxOf(target);
  if (!found) return;
  const { root, ctx } = found;
  if (target.closest('.zb-picker fieldset.zb-size')) {
    // a radio is already checked when its click is dispatched, so "was it checked before" comes from the last known checked size
    const wasChecked = ctx.lastSize === target.value;
    ctx.lastSize = target.value;
    if (!event.isTrusted || !ctx.gatedProduct) {
      // programmatic .click() never opens the gate (today's rule); while open, one that moves the size away from the pick closes it
      if (!event.isTrusted && ctx.gatedProduct && ctx.chosenSize !== null && target.value !== ctx.chosenSize) close(root, ctx);
      return;
    }
    ctx.focusPick = event.detail === 0 ? { colour: false, value: target.value } : null;
    if (!ctx.ready) {
      ctx.rememberedRadio = target;
      return;
    }
    pickSize(root, ctx, target, wasChecked);
  } else if (target.hasAttribute('data-zb-colour-value')) {
    if (ctx.chosenSize === null) return; // gated: hz/41's gated price handles it
    const colour = target.getAttribute('data-zb-colour-value');
    const id = variantFor(ctx, colour, ctx.chosenSize);
    ctx.focusPick = event.detail === 0 ? { colour: true, value: colour } : null;
    if (id === null) {
      close(root, ctx);
      return;
    }
    const changed = id !== ctx.expectedId;
    ctx.expectedId = id;
    root.setAttribute('data-zb-stock', ctx.state.variants[id].st);
    if (changed) announce(root, ctx);
    emitState(root);
  }
}

function onPickChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
  const found = ctxOf(target);
  if (!found) return;
  if (target.closest('.zb-picker fieldset.zb-size')) found.ctx.lastSize = target.value;
  if (found.ctx.gatedProduct && (target.closest('.zb-picker fieldset.zb-size') || target.hasAttribute('data-zb-colour-value'))) scheduleReconcile(found.root, found.ctx);
}

/** The size value that is checked now (null when none): the "already checked" snapshot of the next tap. */
function syncLastSize(root) {
  const ctx = ctxs.get(root);
  if (!ctx) return;
  const checked = sizeRadios(root).find((input) => input.checked);
  ctx.lastSize = checked ? checked.value : null;
}

/** After the picker morph (3e): Horizon's answer must be the variant the shopper picked; focus comes back to the picked radio. */
function checkAnswer(root, detail, isLatest) {
  const ctx = ctxs.get(root);
  if (!ctx || ctx.chosenSize === null) return;
  if (isLatest) {
    const resource = detail && detail.resource;
    if (!resource || String(resource.id) !== ctx.expectedId) {
      close(root, ctx);
      return;
    }
    // Horizon may have written the data-variant-id of an earlier pick (a size picked during a pending colour fetch); its own
    // deferred write may still come after this point, which the scheduled reconcile repairs
    reconcileUrl(root, ctx, ctx.changeGen);
  }
  const pick = ctx.focusPick;
  ctx.focusPick = null;
  if (pick && (document.activeElement === document.body || !document.activeElement)) {
    const radios = pick.colour ? Array.from(root.querySelectorAll('input[data-zb-colour-value]')) : sizeRadios(root);
    const radio = radios.find((input) => (pick.colour ? input.getAttribute('data-zb-colour-value') : input.value) === pick.value);
    if (radio) radio.focus({ preventScroll: true });
  }
}

/** A morph of the add button (Horizon product-form.js:817-820) drops attributes the server markup does not carry: bring the busy state back. */
function reapplyBusy(root) {
  const ctx = ctxs.get(root);
  const form = root.querySelector(FORM_SELECTOR);
  if (ctx && form && ctx.busy) setBusy(ctx, form, true);
}

/** Callbacks run after every resolved ProductSelectEvent of a picker in the root: (root, { html, resource }, isLatest). Later topics push here. */
const afterMorph = [
  (root) => applyPressed(root), // the picker morph brings aria-pressed back to the server's US
  (root) => syncLastSize(root), // the morph may have moved `checked` (Shopify answered another size)
  (root, detail, isLatest) => checkAnswer(root, detail, isLatest),
  (root) => reapplyBusy(root),
  (root) => syncGatedPrice(root), // product-price.js replaced the container
  (root) => emitState(root),
];
let productSelectHooked = false;

/** The latest promise is done (resolved or rejected): the root is no longer pending. Returns whether this promise was the latest.
    The caller emits zb:pdp-state afterwards (the afterMorph chain on a resolve, the rejection handler otherwise). */
function settle(root, ctx, promise) {
  if (!ctx || ctx.pendingPromise !== promise) return false;
  ctx.pendingPromise = null;
  root.removeAttribute('data-zb-pending');
  return true;
}

/** Horizon's product-select event bubbles from <variant-picker>. Listening on document and registering .then on the promise
    runs after the price / form handlers of Horizon (they registered earlier, on the section). AbortError on fast clicks is normal. */
function hookProductSelect() {
  if (productSelectHooked) return;
  const eventName = StandardEvents && StandardEvents.productSelect;
  if (!eventName) return;
  productSelectHooked = true;
  document.addEventListener(eventName, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = target.closest(ROOT_SELECTOR);
    if (!root || !started.has(root) || !target.closest('variant-picker')) return;
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;
    const ctx = ctxs.get(root);
    if (ctx) {
      ctx.pendingPromise = promise;
      root.setAttribute('data-zb-pending', '');
      emitState(root); // pending: true
    }
    promise.then(
      (result) => {
        const isLatest = settle(root, ctx, promise);
        const detail = (result && result.detail) || {};
        afterMorph.forEach((callback) => {
          try {
            callback(root, { html: detail.html, resource: detail.resource }, isLatest);
          } catch (error) {
            // one callback must not stop the others
          }
        });
      },
      (error) => {
        // rejected: aborted by a newer pick (normal), or a real failure, which must not leave a chosen size on screen (DNR-7)
        const isLatest = settle(root, ctx, promise);
        if (ctx && ctx.chosenSize !== null && !(error && error.name === 'AbortError')) close(root, ctx);
        else if (isLatest) emitState(root); // pending: false
      }
    );
  });
}

/** 3h: "Select your size" takes the shopper to the size group. Today ATL product.liquid:1166-1196 (without the skeleton wait). */
function initSelectSize(root) {
  root.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-zb-select-size]') : null;
    if (!button || !root.contains(button)) return;
    const fieldset = root.querySelector('.zb-picker fieldset.zb-size');
    if (!fieldset) return;
    const reduce = reducedMotion();
    fieldset.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    const first = sizeRadios(root)[0];
    if (first) first.focus({ preventScroll: true });
    if (!reduce) {
      fieldset.classList.add('zb-size-nudge');
      setTimeout(() => fieldset.classList.remove('zb-size-nudge'), 600);
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   3i: the add path (contract B). Horizon's own submit handling never runs for the Zerbay product form.
   --------------------------------------------------------------------------------------------------------------------- */

/** window.Zerbay.cartLock, or the cart part's ready event for up to 5 s (contract B.1). null = none came. */
function getCartLock() {
  const now = window.Zerbay && window.Zerbay.cartLock;
  if (now) return Promise.resolve(now);
  return new Promise((resolve) => {
    let timer = 0;
    const onReady = () => {
      clearTimeout(timer);
      document.removeEventListener('zerbay:cart-lock-ready', onReady);
      resolve((window.Zerbay && window.Zerbay.cartLock) || null);
    };
    timer = setTimeout(() => {
      document.removeEventListener('zerbay:cart-lock-ready', onReady);
      resolve(null);
    }, 5000);
    document.addEventListener('zerbay:cart-lock-ready', onReady);
  });
}

function addButtons(form) {
  return Array.from(form.querySelectorAll('add-to-cart-component button'));
}

function setBusy(ctx, form, busy) {
  ctx.busy = busy;
  addButtons(form).forEach((button) => {
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  });
}

/**
 * The variant id an add may post RIGHT NOW, or null. Read from the live state every time (before the lock and again inside
 * the writer): the gate is open, no variant change is in flight, the form's hidden id is the id the shopper picked
 * (ctx.expectedId) and the island says that variant is in stock (PDPC-27). A size the shopper did not pick, or a variant with
 * no stock, is never posted.
 */
function liveAddId(root, ctx, form) {
  if (ctx.gatedProduct && isGated(root)) return null;
  if (ctx.pendingPromise) return null;
  const input = form.querySelector('input[name="id"]');
  const id = input ? String(input.value) : '';
  if (!id) return null;
  if (ctx.gatedProduct && id !== ctx.expectedId) return null;
  const variants = ctx.state && ctx.state.variants;
  const entry = variants && variants[id];
  if (!entry || entry.st !== 'in_stock') return null;
  return id;
}

/** The writer of contract B.2: runs inside the cart lock (its turn may come long after the tap); ctx = { signal, fetch }. */
function makeWriter(root, ctx, form, tapId) {
  return async ({ fetch: lockFetch, signal }) => {
    const target = form.closest('product-form-component') || form;
    const unavailable = () => {
      // no CartErrorEvent after the lock's abort (its watchdog answers TIMEOUT): the lock owns the outcome then
      if (!(signal && signal.aborted)) {
        target.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      }
      return { ok: false, code: 'SERVICE_UNAVAILABLE', message: null };
    };
    // the shopper may have changed the picks while this add waited for its turn: re-run the guard against the live state,
    // before any request. A refusal is silent (nothing was added, nothing to tell).
    const id = liveAddId(root, ctx, form);
    if (id === null || id !== tapId) return { ok: false, code: 'INVALID', message: null, silent: true };
    try {
      const formData = new FormData(form);
      formData.set('id', id); // the id that was just validated, and the id the event below reports
      formData.set('quantity', '1');
      const sectionIds = [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
        if (item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));

      const init = fetchConfig('javascript', { body: formData });
      // Accept text/html is what Horizon's own add request sends too; the rendered sections come from the `sections` parameter, not from this header
      const added = await lockFetch(route('cart_add_url', '/cart/add.js'), { ...init, headers: { ...init.headers, Accept: 'text/html' } });
      const response = await added.json();
      if (response && response.status) {
        target.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        // Shopify's description only (contract B.2); no description -> null, the page shows custom.cart.error
        const description = typeof response.description === 'string' && response.description ? response.description : null;
        return { ok: false, code: 'INVALID', message: description };
      }
      if (!added.ok) return unavailable(); // an error answer without a status field: the add may or may not have happened
      const cartResponse = await lockFetch(`${route('cart_url', '/cart')}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      target.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: id, quantity: 1 }],
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              sections: response && response.sections,
              itemCount: cart.item_count,
              source: 'zerbay-product-form',
              didError: false,
            },
          }),
        })
      );
      return { ok: true, cart };
    } catch (error) {
      return unavailable();
    }
  };
}

/** One add at a time per page. Order: gate, running add, pending variant change, stock guard, lock (the writer guards again). A refused add takes no lock. */
async function handleAdd(root, ctx, form) {
  if (ctx.gatedProduct && isGated(root)) return;
  if (ctx.adding) return;
  const intended = ctx.expectedId; // the variant the shopper had picked at the tap (before any pending change settles)
  ctx.adding = true;
  let reset = true; // false only while the ok-path timer holds the busy look
  try {
    for (let guard = 0; ctx.pendingPromise && guard < 20; guard += 1) {
      try {
        await ctx.pendingPromise;
      } catch (error) {
        // a failed or aborted variant change is handled by the picker hook
      }
    }
    // the variant at tap time (after a pending change settled) is the one that is added; the writer refuses if it moved
    const tapId = liveAddId(root, ctx, form);
    if (tapId === null) return;
    // a pick made after the tap (while the earlier change was pending) is not what this tap asked for: no add, nothing to tell
    if (ctx.gatedProduct && tapId !== intended) return;

    setBusy(ctx, form, true);
    clearAddError(root);

    const lock = await getCartLock();
    if (!lock || typeof lock.run !== 'function') {
      setBusy(ctx, form, false);
      showAddError(root, ctx, null);
      return;
    }
    let result = null;
    try {
      result = await lock.run('add', makeWriter(root, ctx, form, tapId));
    } catch (error) {
      result = null;
    }
    if (result && result.ok) {
      reset = false;
      setTimeout(() => {
        setBusy(ctx, form, false);
        ctx.adding = false;
      }, 1000);
      return;
    }
    setBusy(ctx, form, false);
    if (result && result.silent) return; // the writer found the picks changed while it waited: nothing was sent, nothing to say
    showAddError(root, ctx, result && typeof result.message === 'string' && result.message ? result.message : null);
  } finally {
    if (reset) ctx.adding = false;
  }
}

let globalListeners = false;

/** Window-level capture listeners, registered once per page. The hz/40 inline backstop registered earlier, so while the gate is
    closed it refuses first. */
function installGlobalListeners() {
  if (globalListeners) return;
  globalListeners = true;
  window.addEventListener('click', onPickClick, true);
  window.addEventListener('change', onPickChange, true);
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
      const found = ctxOf(form);
      if (!found) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      handleAdd(found.root, found.ctx, form);
    },
    true
  );
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('add-to-cart-component button') : null;
      if (!button || !button.closest(ROOT_SELECTOR)) return;
      // Horizon's AddToCartComponent.handleClick animations never run; the default action still submits the form
      event.stopImmediatePropagation();
    },
    true
  );
}

/** 3b: the gate may open only after the add handler is installed and product-form-component and variant-picker are defined. */
async function becomeReady(root, ctx) {
  try {
    // the form owns the hidden id; the picker owns the variant switch (a pick before it exists is replayed below)
    await Promise.all([customElements.whenDefined('product-form-component'), customElements.whenDefined('variant-picker')]);
  } catch (error) {
    return;
  }
  ctx.ready = true;
  if (!ctx.gatedProduct || !isGated(root)) return;
  if (ctx.rememberedRadio !== null) {
    // a trusted size pick arrived before Horizon existed; a later programmatic click may have moved `checked` since
    const radio = ctx.rememberedRadio;
    ctx.rememberedRadio = null;
    const id = radio.isConnected && root.contains(radio) ? variantFor(ctx, checkedColour(root), radio.value) : null;
    if (id === null) {
      safeEnsureMarker(root); // the pick cannot be resolved: the gate stays closed (and a keep_size suspension ends)
      return;
    }
    const moved = !radio.checked;
    if (moved) radio.checked = true;
    ctx.lastSize = radio.value;
    open(root, ctx, radio.value, id, true);
    resumeMarker(root); // open: writes nothing, ends a keep_size suspension
    // Horizon never saw that pick (no change handler yet), so the form may still hold the old variant: let the picker switch now
    if (moved || formVariantId(root) !== id) radio.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }
  if (!ctx.keepSizeArrival) return;
  // keep_size arrival: the server checked a size, the shopper did not pick one; open only for the variant the SERVER selected
  // (a click that moved the picks before this module ran must not open the gate), and only when the form agrees
  const checked = sizeRadios(root).find((input) => input.checked);
  const id = checked ? variantFor(ctx, checkedColour(root), checked.value) : null;
  if (ctx.arrivalId === null || id === null || id !== ctx.arrivalId || formVariantId(root) !== id) {
    safeEnsureMarker(root);
    return;
  }
  open(root, ctx, checked.value, id, false, false);
  resumeMarker(root);
}

/* ---------------------------------------------------------------------------------------------------------------------
   hz/47: size guide (PDPC-24). Markup: snippets/zerbay-size-guide.liquid (a Horizon <dialog-component>; Horizon's DialogComponent
   opens, closes, handles Escape and the backdrop and dispatches dialog:open / dialog:close on the host). This code only adds what
   Zerbay needs: the stock cells and the chosen row, the page scroll lock with the Inbox bubble hidden (html.zb-scroll-lock) and the
   focus (close button on open, trigger on close). The trigger lives in the picker, which Horizon morphs, so it is looked up again
   at close time.
   --------------------------------------------------------------------------------------------------------------------- */

const SG_HOST_SELECTOR = 'dialog-component.zb-sg-host';
const SG_TRIGGER_SELECTOR = '.zb-sg-open';
const SG_LOCK = 'zb-scroll-lock';

/** The EU number of a size value such as "US8-EU41-UK7" ("41"), or "". */
function sgEu(value) {
  const match = /EU\s*(\d+(?:\.\d+)?)/i.exec(String(value === null || value === undefined ? '' : value));
  return match ? match[1] : '';
}

/**
 * Fills the stock cells for the checked colour (a size that is not in_stock gets the sold-out word) and marks the chosen row.
 * The row is marked only while the gate is open and the shopper has a chosen size; a gated page marks no row.
 */
function sgFill(root, dialog) {
  const state = stateOf(root);
  const ctx = ctxs.get(root);
  const word = dialog.getAttribute('data-zb-sg-soldout') || '';
  const colour = checkedColour(root);
  const byEu = {};
  const variants = state && state.variants && typeof state.variants === 'object' ? state.variants : null;
  if (variants && colour !== null) {
    Object.keys(variants).forEach((id) => {
      const entry = variants[id];
      if (!entry || entry.c !== colour) return;
      const eu = sgEu(entry.s);
      if (eu) byEu[eu] = entry.st;
    });
  }
  dialog.querySelectorAll('[data-zb-sg-eu]').forEach((cell) => {
    const status = byEu[cell.getAttribute('data-zb-sg-eu')];
    const text = status && status !== 'in_stock' ? word : '';
    if (cell.textContent !== text) cell.textContent = text;
  });
  const chosenEu = ctx && ctx.chosenSize !== null && !isGated(root) ? sgEu(ctx.chosenSize) : '';
  dialog.querySelectorAll('[data-zb-sg-row]').forEach((row) => {
    row.classList.toggle('zb-sg__row--chosen', chosenEu !== '' && row.getAttribute('data-zb-sg-row') === chosenEu);
  });
}

/**
 * Removes the Zerbay scroll-lock class unless another connected modal dialog still needs it (cart drawer, country sheet, menu).
 * Same pattern as zerbay-header.js releaseScrollLock and zerbay-country-sheet.js #cleanup.
 */
function sgUnlock() {
  let other = null;
  try {
    other = document.querySelector('dialog:modal');
  } catch (_) {
    other = document.querySelector('dialog[open]');
  }
  if (!other) document.documentElement.classList.remove(SG_LOCK);
}

function initSizeGuide(root) {
  const host = root.querySelector(SG_HOST_SELECTOR);
  const dialog = host ? host.querySelector('dialog') : null;
  if (!host || !dialog) return;
  const refill = () => {
    if (dialog.open) sgFill(root, dialog);
  };
  // while open: if the host leaves the page without a section-unload event (a section node removed directly), drop the lock
  let leaveWatch = null;
  const watchOff = () => {
    if (leaveWatch) leaveWatch.disconnect();
    leaveWatch = null;
  };
  const watchOn = () => {
    watchOff();
    if (typeof MutationObserver !== 'function') return;
    leaveWatch = new MutationObserver(() => {
      if (host.isConnected) return;
      watchOff();
      unlockScroll(dialog);
      sgUnlock();
    });
    leaveWatch.observe(document.documentElement, { childList: true, subtree: true });
  };
  // fill before the dialog shows (Horizon opens it a frame after the click), and again when it has opened
  root.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest(SG_TRIGGER_SELECTOR)) sgFill(root, dialog);
  });
  host.addEventListener('dialog:open', () => {
    sgFill(root, dialog);
    document.documentElement.classList.add(SG_LOCK);
    watchOn();
    const close = dialog.querySelector('.zb-sg__close');
    if (close) close.focus({ preventScroll: true });
  });
  // A native close request (dialog.requestClose(), the Android back gesture, a close watcher) fires `cancel` and then closes the
  // dialog without Horizon's closeDialog: route it through closeDialog (animation end, Horizon's unlockScroll, dialog:close).
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    host.closeDialog();
  });
  // Backdrop press-origin guard (as today): a press that starts inside the panel and ends on the backdrop (a text selection dragged
  // out) must not close it. The click then targets the dialog itself although the press did not start there. This listener sits on
  // the dialog, Horizon's click handler on the host (an ancestor), so the bubbling click reaches this one first and can stop it.
  let downOnBackdrop = false;
  dialog.addEventListener('pointerdown', (event) => {
    downOnBackdrop = event.target === dialog;
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog && !downOnBackdrop) event.stopPropagation();
    downOnBackdrop = false;
  });
  // Fallback when the cancel could not be cancelled (or the dialog closed some other way): clear the Horizon lock owner and ours.
  const closed = () => {
    if (dialog.open) return;
    watchOff();
    unlockScroll(dialog);
    sgUnlock();
  };
  dialog.addEventListener('close', closed);
  host.addEventListener('dialog:close', () => {
    closed();
    const trigger = root.querySelector(SG_TRIGGER_SELECTOR);
    if (trigger) trigger.focus({ preventScroll: true });
  });
  // a colour or size change, a gate change or a unit change while the dialog is open
  root.addEventListener('zb:pdp-state', refill);
  root.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.matches('input[data-zb-colour-value]')) refill();
  });
}

/** One call per host root. Later topics add their own setup here. */
function init(root) {
  if (started.has(root)) return;
  started.add(root);
  states.set(root, readState(root));
  const ctx = makeCtx(root);
  ctxs.set(root, ctx);
  syncLastSize(root);
  initUnits(root);
  initGatedPrice(root);
  initSelectSize(root);
  initColourFeedback(root);
  initSizeGuide(root);
  hookProductSelect();
  initMarker(root);
  installGlobalListeners();
  emitState(root);
  becomeReady(root, ctx);
}

function start() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(init);
}

// theme editor: a re-rendered section brings a new root (init is guarded per root)
document.addEventListener('shopify:section:load', start);
// theme editor: an unloaded section can leave its open size guide behind
document.addEventListener('shopify:section:unload', () => setTimeout(sgUnlock, 0));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
